import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Icon,
  Input,
  Link,
  Pagination,
  Popover,
  RadioGroup,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import { CollectionPreferencesProps } from '@amzn/awsui-components-react-v3/uxdg';
import { ceil, cloneDeep } from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { isObjectDefined, validateInput } from '../../../utils';
import { Category, ContainerSearchResult } from '../../../containers/ContainerManagement/types';
import {
  ContainerDefinition,
  DeleteContainerRequest,
  UpdateContainerRequest,
} from '../../../services/container-management/types';
import { getSubcategoryOptions } from '../../../utils/ContainerManagement/containerDefinitionsHelper';
import { getBlocksFromRules } from '../../../utils/ContainerManagement/containerSearchHelper';
import { DELETE_CONTAINER, UPDATE_CONTAINER } from '../../../services/container-management/queries';
import './style.css';
import {
  cmsMigrationFeatureFlag,
  zoneFeatureFlag,
  capacityFilterFlag,
} from '../../../utils/featureFlags';
import { SearchVariables } from '../ContainerSearch';

const SORTING_FIELD_TO_SERVER_SORTING_COLUMN = new Map([
  ['server-name', 'fq_container_name'],
  ['server-type', 'container_type'],
  ['server-category', 'category'],
  ['server-subcategory', 'subcategory'],
  ['server-location', 'fq_location'],
  ['server-rules', 'policy.sort_comparator'],
  ['server-status', 'state'],
  ['server-size', 'size'],
  ['server-sealId', 'sealIds'],
  ['server-cctvRequired', 'cctv_required'],
  ['server-createdBy', 'created_by'],
  ['server-lastUpdatedUser', 'last_updated_by'],
  ['server-creationTime', 'creation_timestamp'],
  ['server-lastUpdatedTime', 'last_updated_timestamp'],
  ['server-hasOverriddenRules', 'has_overridden_rules'],
  ['server-isCompliant', 'metadata_isCompliant'],
  ['server-isEmpty', 'metadata_isEmpty'],
  ['server-zone', 'metadata_location_room.zone'],
  ['server-parentContainerName', 'parent_container.fq_name'],
  ['server-parentContainerType', 'parent_container.type'],
  ['server-userAlias', 'user_custody'],
]);

const SUCCESS = 'SUCCESS';

export type SearchOptions = {
  pageSize: number;
  sortingColumn: string;
  sortingDescending: boolean;
};

type EditInput = {
  section: string;
  aisle: string;
  level: string;
  alphabetIdentifier: string;
  cell: string;
  category: SelectProps.Option | null;
  subcategory: SelectProps.Option | null;
  isCompliant?: boolean;
};

const emptyEditInput = {
  section: '',
  aisle: '',
  level: '',
  alphabetIdentifier: '',
  cell: '',
  category: null,
  subcategory: null,
};

export const renderIsActiveField = (status: string): string => {
  return status; // Could be ACTIVE, INACTIVE, ARCHIVED
};

export const renderBooleanAsYesNo = (val: boolean): string => {
  return val === true ? 'Yes' : 'No';
};

export const renderBooleanAsIsEmpty = (val: boolean): string => {
  return val === true ? 'Empty' : 'Contains parts';
};

export const renderStringAsYesNo = (val: string): string => {
  return val === 'true' ? 'Yes' : 'No';
};

export default function ContainerSearchResultsTable(props: {
  searchResults: ContainerSearchResult[];
  totalResults: number;
  pageIndex: number;
  categoryOptions: SelectProps.Option[];
  categoriesMap: Map<string, Category> | null;
  containerDefinitionMap: Map<string, ContainerDefinition> | null;
  managePermissions: boolean;
  setPageIndex: (index: number) => void;
  setSearchOptions: (searchOptions: SearchOptions) => void;
  downloadSelectedContainers: (containers: ContainerSearchResult[]) => void;
  downloadAllContainers: () => void;
  handleTypeChange: (type: string) => void;
  searchVariables: SearchVariables | undefined;
}): ReactElement {
  const {
    searchResults,
    totalResults,
    pageIndex,
    categoryOptions,
    categoriesMap,
    containerDefinitionMap,
    managePermissions,
    setPageIndex,
    setSearchOptions,
    downloadSelectedContainers,
    downloadAllContainers,
    handleTypeChange,
    searchVariables,
  } = props;
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [sortingColumn, setSortingColumn] = useState<any>(null);
  const [serverSortingColumn, setServerSortingColumn] = useState<string>('');
  const [sortingDescending, setSortingDescending] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedSearchResults, setEditedSearchResults] = useState<ContainerSearchResult[]>([]);
  const [editInput, setEditInput] = useState<EditInput>(emptyEditInput);
  const [idToServerError, setIdToServerError] = useState<Map<string, string>>(new Map());
  const [currentContainer, setCurrentContainer] = useState<ContainerSearchResult | null>(null);
  const [containerDefinition, setContainerDefinition] = useState<ContainerDefinition | null>(null);
  const [sectionError, setSectionError] = useState<string>('');
  const [aisleError, setAisleError] = useState<string>('');
  const [levelError, setLevelError] = useState<string>('');
  const [alphabetIdentifierError, setAlphaBetIdentifierError] = useState<string>('');
  const [cellError, setCellError] = useState<string>('');
  const [categoryError, setCategoryError] = useState<string>('');
  const [subcategoryError, setSubcategoryError] = useState<string>('');
  const showMigrationFeature = cmsMigrationFeatureFlag();
  const zoneFeatureLaunched = zoneFeatureFlag();
  const containerCapacityFeature = capacityFilterFlag();
  const [updateResponse, setUpdateResponse] = useState(null);
  const [updateContainerError, setUpdateContainerError] = useState<string>('');
  const [deleteResponse, setDeleteResponse] = useState(null);
  const [deleteContainerError, setDeleteContainerError] = useState<string>('');
  const [isMovableTypeSelected, setIsMovableTypeSelected] = useState(false);

  const [updateContainer, { loading: updateContainerLoading }] = useMutation(UPDATE_CONTAINER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setUpdateResponse(result.updateContainer);
    },
    onError: (error) => {
      setUpdateContainerError(error.message);
    },
  });

  const callUpdateContainer = (request: UpdateContainerRequest): void => {
    updateContainer({
      variables: {
        ...request,
      },
    });
  };

  const [deleteContainer, { loading: deleteContainerLoading }] = useMutation(DELETE_CONTAINER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setDeleteResponse(result.deleteContainer);
    },
    onError: (error) => {
      setDeleteContainerError(error.message);
    },
  });

  const callDeleteContainer = (request: DeleteContainerRequest): void => {
    deleteContainer({
      variables: {
        ...request,
      },
    });
  };

  const handleUpdateContainer = (e: ContainerSearchResult, input: EditInput) => {
    setUpdateContainerError('');
    setIdToServerError(cloneDeep(idToServerError.set(e.containerId, '')));
    const reasonCodes = [];
    // there is only one name reason code, which is 400, "name correction change"
    if (isContainerNameUpdated(e, editInput)) {
      reasonCodes.push(400);
    }

    // there is only one reason code for category/subcategory, which is 401, "migration change code"
    if (isContainerDefUpdated(e, editInput)) {
      reasonCodes.push(401);
    }

    if (isContainerComplianceUpdated(e, editInput)) {
      reasonCodes.push(402);
    }

    const request: UpdateContainerRequest = {
      containerId: e.containerId,
      version: parseInt(e.current_version, 10) || 0,
      reasonCodes,
    };
    if (isContainerNameUpdated(e, editInput)) {
      request.metadataStruct = {
        ...request.metadataStruct,
        section: editInput.section,
        aisle: editInput.aisle,
        level: editInput.level,
        alphabetIdentifier: editInput.alphabetIdentifier,
      };
      request.containerName = editInput.cell;
    }
    if (isContainerDefUpdated(e, editInput)) {
      request.containerDefinitionId = editInput.subcategory?.value;
    }
    if (isContainerComplianceUpdated(e, editInput)) {
      request.metadataStruct = {
        ...request.metadataStruct,
        isCompliant: editInput.isCompliant,
      };
    }
    callUpdateContainer(request);
  };

  const handleDeleteContainer = (e: ContainerSearchResult) => {
    setDeleteContainerError('');
    setIdToServerError(cloneDeep(idToServerError.set(e.containerId, '')));

    const request: DeleteContainerRequest = {
      containerId: e.containerId,
      version: parseInt(e.current_version, 10) || 0,
      reasonCode: 0,
    };
    callDeleteContainer(request);
  };

  useEffect(() => {
    if (
      updateResponse &&
      currentContainer &&
      editInput.subcategory?.value &&
      !isMovableTypeSelected
    ) {
      setIdToServerError(cloneDeep(idToServerError.set(currentContainer.containerId, SUCCESS)));
      const newEditedSearchResults = cloneDeep(editedSearchResults);
      for (let i = 0; i < newEditedSearchResults.length; i += 1) {
        if (newEditedSearchResults[i].containerId === currentContainer.containerId) {
          newEditedSearchResults[i] = {
            ...newEditedSearchResults[i],
            containerDefinitionId: editInput.subcategory.value,
            category: editInput.category?.label ?? '',
            subcategory: editInput.subcategory?.label ?? '',
            name: `${newEditedSearchResults[i].site.name}.
                    ${newEditedSearchResults[i].room.name}.
                    ${editInput.alphabetIdentifier}
                    ${editInput.section}
                    ${editInput.aisle}
                    ${editInput.level}
                    ${editInput.cell}`.replace(/\s+/g, ''),
            alphabetIdentifier: editInput.alphabetIdentifier,
            section: editInput.section,
            aisle: editInput.aisle,
            level: editInput.level,
            cell: editInput.cell,
            // eslint-disable-next-line @typescript-eslint/camelcase
            current_version: String(
              parseInt(newEditedSearchResults[i].current_version, 10) || 0 + 1
            ),
          };
          break;
        }
      }
      setEditedSearchResults(newEditedSearchResults);
      setCurrentContainer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResponse]);

  useEffect(() => {
    if (
      updateResponse &&
      currentContainer &&
      editInput.isCompliant !== currentContainer.isCompliant
    ) {
      setIdToServerError(cloneDeep(idToServerError.set(currentContainer.containerId, SUCCESS)));
      const newEditedSearchResults = cloneDeep(editedSearchResults);
      for (let i = 0; i < newEditedSearchResults.length; i += 1) {
        if (newEditedSearchResults[i].containerId === currentContainer.containerId) {
          newEditedSearchResults[i] = {
            ...newEditedSearchResults[i],
            isCompliant: editInput.isCompliant,
            // eslint-disable-next-line @typescript-eslint/camelcase
            current_version: String(
              parseInt(newEditedSearchResults[i].current_version, 10) || 0 + 1
            ),
          };
          break;
        }
      }
      setEditedSearchResults(newEditedSearchResults);
      setCurrentContainer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResponse]);

  useEffect(() => {
    if (deleteResponse && currentContainer) {
      setIdToServerError(cloneDeep(idToServerError.set(currentContainer.containerId, SUCCESS)));
      const newEditedSearchResults = editedSearchResults.filter(
        (container) => container.containerId !== currentContainer.containerId
      );
      setEditedSearchResults(newEditedSearchResults);
      setCurrentContainer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteResponse]);

  useEffect(() => {
    if (updateContainerError && updateContainerError.length > 0 && currentContainer) {
      setIdToServerError(
        cloneDeep(idToServerError.set(currentContainer.containerId, updateContainerError))
      );
      setCurrentContainer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateContainerError]);

  useEffect(() => {
    if (deleteContainerError && deleteContainerError.length > 0 && currentContainer) {
      setIdToServerError(
        cloneDeep(idToServerError.set(currentContainer.containerId, deleteContainerError))
      );
      setCurrentContainer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteContainerError]);

  const columnDefinitions = [
    {
      id: 'name',
      header: t('Name'),
      cell: (e: ContainerSearchResult) => (
        <Link href={`/assets-inventory/container/${e.containerId}`}>{e.name}</Link>
      ),
      sortingField: 'server-name',
    },
    {
      id: 'containerType',
      header: t('Type'),
      cell: (e: ContainerSearchResult) => e.containerType,
      sortingField: 'server-type',
    },
    {
      id: 'category',
      header: t('Category'),
      cell: (e: ContainerSearchResult) => e.category,
      sortingField: 'server-category',
    },
    {
      id: 'subcategory',
      header: t('Subcategory'),
      cell: (e: ContainerSearchResult) => e.subcategory,
      sortingField: 'server-subcategory',
    },
    {
      id: 'location',
      header: t('Location'),
      cell: (e: ContainerSearchResult) => {
        const elements: ReactElement[] = [];
        if (e.site && e.site.name) {
          elements.push(<span>{e.site.name}</span>);
        }
        if (e.room && e.room.name) {
          elements.push(
            <Icon variant="disabled" name="caret-right-filled" />,
            <span>{e.room.name}</span>
          );
        }
        if (e.section) {
          elements.push(
            <Icon variant="disabled" name="caret-right-filled" />,
            <span>{e.section}</span>
          );
        }
        if (e.aisle) {
          elements.push(
            <Icon variant="disabled" name="caret-right-filled" />,
            <span>{e.aisle}</span>
          );
        }
        if (e.level) {
          elements.push(
            <Icon variant="disabled" name="caret-right-filled" />,
            <span>{e.level}</span>
          );
        }
        return <div>{elements}</div>;
      },
      sortingField: 'server-location',
    },
    {
      id: 'rules',
      header: t('Rules'),
      cell: (e: ContainerSearchResult) => {
        const element: ReactElement[] = [];
        getBlocksFromRules(e.policy?.rule).forEach((block) => {
          element.push(<Badge color="grey">{block}</Badge>);
          element.push(<span>&nbsp;</span>);
        });
        return <div>{element}</div>;
      },
      sortingField: 'server-rules',
    },
    {
      id: 'status',
      header: t('State'),
      cell: (e: ContainerSearchResult) => renderIsActiveField(e.state),
      sortingField: 'server-status',
    },
    {
      id: 'cctvRequired',
      header: t('CCTV Coverage'),
      cell: (e: ContainerSearchResult) => renderStringAsYesNo(e.cctvRequired),
      sortingField: 'server-cctvRequired',
    },
    {
      id: 'seal_ids',
      header: t('Seal IDs'),
      cell: (e: ContainerSearchResult) => {
        const element: ReactElement[] = [];
        if (e.seal_ids) {
          e.seal_ids.forEach((sealId) => {
            element.push(<Badge color="grey">{sealId}</Badge>);
            element.push(<span>&nbsp;</span>);
          });
        }
        return <div>{element}</div>;
      },
      sortingField: 'server-sealId',
    },
    {
      id: 'ticket_ids',
      header: t('Ticket IDs'),
      cell: (e: ContainerSearchResult) => {
        const element: ReactElement[] = [];
        if (e.ticket_ids) {
          e.ticket_ids.forEach((ticketId) => {
            element.push(<Badge color="grey">{ticketId}</Badge>);
            element.push(<span>&nbsp;</span>);
          });
        }
        return <div>{element}</div>;
      },
      sortingField: 'server-ticketId',
    },
    {
      id: 'createdBy',
      header: t('Created By'),
      cell: (e: ContainerSearchResult) => e.createdBy,
      sortingField: 'server-createdBy',
    },
    {
      id: 'lastUpdatedUser',
      header: t('Last Updated By'),
      cell: (e: ContainerSearchResult) => e.lastUpdatedUser,
      sortingField: 'server-lastUpdatedUser',
    },
    {
      id: 'creationTime',
      header: t('Creation Time'),
      cell: (e: ContainerSearchResult) => e.creationTime,
      sortingField: 'server-creationTime',
    },
    {
      id: 'lastUpdatedTime',
      header: t('Last Update Time'),
      cell: (e: ContainerSearchResult) => e.lastUpdatedTime,
      sortingField: 'server-lastUpdatedTime',
    },
    {
      id: 'hasOverriddenRules',
      header: t('Overridden Rules'),
      cell: (e: ContainerSearchResult) => renderBooleanAsYesNo(e.hasOverriddenRules),
      sortingField: 'server-hasOverriddenRules',
    },
    {
      id: 'isCompliant',
      header: t('Compliant'),
      cell: (e: ContainerSearchResult) => {
        if (e.isCompliant === true) {
          return 'Yes';
        }
        return (
          <SpaceBetween direction="horizontal" size="xs">
            No
            <Icon variant="error" name="status-warning" />
          </SpaceBetween>
        );
      },
      sortingField: 'server-isCompliant',
    },
    {
      id: 'isEmpty',
      header: t('Container capacity'),
      cell: (e: ContainerSearchResult) => renderBooleanAsIsEmpty(e.isEmpty),
      sortingField: 'server-isEmpty',
    },
    {
      id: 'zone',
      header: t('Zone'),
      cell: (e: ContainerSearchResult) => e.room.zone,
      sortingField: 'server-zone',
    },
    {
      id: 'parentContainerName',
      header: t('Parent Container'),
      cell: (e: ContainerSearchResult) => (
        <Link href={`/assets-inventory/container/${e.parent_container.id}`}>
          {e.parent_container.fq_name}
        </Link>
      ),
      sortingField: 'server-parentContainerName',
    },
    {
      id: 'parentContainerType',
      header: t('Parent Container Type'),
      cell: (e: ContainerSearchResult) => e.parent_container.type,
      sortingField: 'server-parentContainerType',
    },
    {
      id: 'userAlias',
      header: t('User With Custody'),
      cell: (e: ContainerSearchResult) => e.userAlias,
      sortingField: 'server-userAlias',
    },
    {
      id: 'editName',
      header: t('Name'),
      width: '540px',
      cell: (e: ContainerSearchResult) => editNameSection(e),
    },
    {
      id: 'editCategory',
      header: t('Category'),
      width: '260px',
      cell: (e: ContainerSearchResult) =>
        e.category === 'Default' ? editCategorySection(e) : e.category,
    },
    {
      id: 'editSubcategory',
      header: t('Subcategory'),
      width: '260px',
      cell: (e: ContainerSearchResult) =>
        e.subcategory === 'Default' ? editSubcategorySection(e) : e.subcategory,
    },
    {
      id: 'editIsCompliant',
      header: t('Is Label Printed'),
      width: '260px',
      cell: (e: ContainerSearchResult) => editIsCompliantSection(e),
    },
    {
      id: 'editActions',
      header: t('Actions'),
      width: '280px',
      cell: (e: ContainerSearchResult) => editActionSection(e),
    },
  ];

  const defaultColumns = [
    'name',
    'containerType',
    'category',
    'subcategory',
    'location',
    'isCompliant',
    'status',
  ];

  const defaultColumnsForMovableContainers = [
    'name',
    'containerType',
    'location',
    'parentContainerName',
    'isCompliant',
    'status',
  ];
  const editModeColumns = ['editName', 'editCategory', 'editSubcategory', 'editActions'];

  const editModeColumnsForMovableContainers = [
    'name',
    'parentContainerName',
    'isEmpty',
    'editIsCompliant',
    'editActions',
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: defaultColumns,
  });

  const { items, collectionProps, paginationProps } = useCollection(editedSearchResults, {
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
  });

  // TODO: Get the list of container types dynamically to eliminate hard-coding types.
  const isMovableContainerType = (type: string): boolean => {
    const movableContainerTypes = new Set(['tote', 'pallet', 'corrugated_box']);
    return movableContainerTypes.has(type);
  };

  useEffect(() => {
    if (searchVariables) {
      setIsMovableTypeSelected(isMovableContainerType(searchVariables?.type));
    }
  }, [searchVariables]);

  useEffect(() => {
    const columnsToSet = isMovableTypeSelected
      ? defaultColumnsForMovableContainers
      : defaultColumns;
    setPreferences((prev) => ({ ...prev, visibleContent: columnsToSet }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMovableTypeSelected]);

  const getVisibleColumns = () => {
    if (showMigrationFeature && editMode) {
      return isMovableTypeSelected ? editModeColumnsForMovableContainers : editModeColumns;
    }
    return preferences.visibleContent;
  };

  const getDefaultEditInput = (e: ContainerSearchResult | null) => {
    if (!e) {
      return emptyEditInput;
    }
    const categoryOption = categoryOptions.find((cat) => cat.label === e.category) ?? null;
    const subcategoryOption =
      getSubcategoryOptions(categoriesMap, categoryOption)?.find(
        (subcat) => subcat.label === e.subcategory
      ) ?? null;

    return {
      section: e.section?.toUpperCase(),
      aisle: e.aisle?.toUpperCase(),
      level: e.level?.toUpperCase(),
      alphabetIdentifier: e.alphabetIdentifier?.toUpperCase(),
      cell: e.cell?.toUpperCase(),
      category: categoryOption,
      subcategory: subcategoryOption,
      isCompliant: e.isCompliant,
    };
  };

  const isContainerNameUpdated = (e: ContainerSearchResult, input: EditInput): boolean => {
    return !(
      input.section === e.section &&
      input.aisle === e.aisle &&
      input.level === e.level &&
      input.alphabetIdentifier === e.alphabetIdentifier &&
      input.cell === e.cell
    );
  };

  const isContainerDefUpdated = (e: ContainerSearchResult, input: EditInput): boolean => {
    return !(input.category?.label === e.category && input.subcategory?.label === e.subcategory);
  };

  const isContainerComplianceUpdated = (e: ContainerSearchResult, input: EditInput): boolean => {
    return !(input.isCompliant === e.isCompliant);
  };

  const changesWereMade = (e: ContainerSearchResult, input: EditInput): boolean => {
    return (
      isContainerNameUpdated(e, input) ||
      isContainerDefUpdated(e, input) ||
      isContainerComplianceUpdated(e, input)
    );
  };

  const editNameSection = (e: ContainerSearchResult) => {
    const siteRoom = `${e.site.name}.${e.room.name}`;
    return e.containerId === currentContainer?.containerId ? (
      <SpaceBetween direction="vertical" size="xs">
        <div
          style={{
            width: `${siteRoom.length + 1}rem`,
          }}
        >
          <Input value={siteRoom} disabled />
        </div>
        <SpaceBetween direction="horizontal" size="xs">
          {isObjectDefined(
            containerDefinition?.metadataConfiguration.alphabetIdentifier?.default
          ) && (
            <SpaceBetween direction="vertical" size="xxxs">
              <Input
                data-test-id="alphabet-identifier-edit-input"
                className="small-input"
                value={editInput.alphabetIdentifier}
                onChange={({ detail }) => {
                  setEditInput({
                    ...editInput,
                    alphabetIdentifier: detail.value.toUpperCase(),
                  });
                }}
                invalid={alphabetIdentifierError.length > 0}
                disabled={editInput.subcategory === null || updateContainerLoading}
              />
              <small className="subtitle">Alphabet</small>
            </SpaceBetween>
          )}
          {isObjectDefined(containerDefinition?.metadataConfiguration.section?.default) && (
            <SpaceBetween direction="vertical" size="xxxs">
              <Input
                data-test-id="section-edit-input"
                className="small-input"
                value={editInput.section}
                onChange={({ detail }) => {
                  setEditInput({
                    ...editInput,
                    section: detail.value.toUpperCase(),
                  });
                }}
                invalid={sectionError.length > 0}
                disabled={editInput.subcategory === null || updateContainerLoading}
              />
              <small className="subtitle">Section</small>
            </SpaceBetween>
          )}
          {isObjectDefined(containerDefinition?.metadataConfiguration.aisle?.default) && (
            <SpaceBetween direction="vertical" size="xxxs">
              <Input
                data-test-id="aisle-edit-input"
                className="small-input"
                value={editInput.aisle}
                onChange={({ detail }) => {
                  setEditInput({
                    ...editInput,
                    aisle: detail.value.toUpperCase(),
                  });
                }}
                invalid={aisleError.length > 0}
                disabled={editInput.subcategory === null || updateContainerLoading}
              />
              <small className="subtitle">Aisle</small>
            </SpaceBetween>
          )}
          {isObjectDefined(containerDefinition?.metadataConfiguration.level?.default) && (
            <SpaceBetween direction="vertical" size="xxxs">
              <Input
                data-test-id="level-edit-input"
                className="small-input"
                value={editInput.level}
                onChange={({ detail }) => {
                  setEditInput({
                    ...editInput,
                    level: detail.value.toUpperCase(),
                  });
                }}
                invalid={levelError.length > 0}
                disabled={editInput.subcategory === null || updateContainerLoading}
              />
              <small className="subtitle">Level</small>
            </SpaceBetween>
          )}
          <SpaceBetween direction="vertical" size="xxxs">
            <Input
              data-test-id="cell-edit-input"
              className="input"
              value={editInput.cell}
              onChange={({ detail }) => {
                setEditInput({
                  ...editInput,
                  cell: detail.value.toUpperCase(),
                });
              }}
              invalid={cellError.length > 0}
              disabled={editInput.subcategory === null || updateContainerLoading}
            />
            <small className="subtitle">Cell</small>
          </SpaceBetween>
        </SpaceBetween>
        {isObjectDefined(containerDefinition?.metadataConfiguration?.alphabetIdentifier?.default) &&
          containerDefinition?.metadataConfiguration?.alphabetIdentifier?.default !==
            editInput.alphabetIdentifier && (
            <small
              data-test-id="alphabet-identifier-warning"
              style={{
                color: 'orange',
              }}
            >
              {t(
                `Recommended Alphabet Identifier is ${containerDefinition?.metadataConfiguration?.alphabetIdentifier?.default}`
              )}
            </small>
          )}
      </SpaceBetween>
    ) : (
      <div>
        {e.name}
        <Button
          data-test-id="name-edit-button"
          iconAlign="right"
          iconName="edit"
          variant="inline-icon"
          onClick={() => setCurrentContainer(e)}
          disabled={updateContainerLoading}
        />
      </div>
    );
  };

  const editCategorySection = (e: ContainerSearchResult) => {
    return e.containerId === currentContainer?.containerId ? (
      <Select
        className="dropdown"
        expandToViewport
        data-test-id="category-dropdown"
        selectedOption={editInput.category}
        onChange={({ detail }) => {
          setEditInput({
            ...editInput,
            category: detail.selectedOption?.value ? detail.selectedOption : null,
            subcategory: null,
          });
        }}
        options={[
          {
            label: '-',
            value: '',
          },
          ...categoryOptions,
        ]}
        placeholder={t('Choose category')}
        empty={t('No options')}
        disabled={updateContainerLoading}
        invalid={categoryError.length > 0}
      />
    ) : (
      <div>
        {e.category}
        <Button
          data-test-id="category-edit-button"
          iconAlign="right"
          iconName="edit"
          variant="inline-icon"
          onClick={() => setCurrentContainer(e)}
          disabled={updateContainerLoading}
        />
      </div>
    );
  };

  const editSubcategorySection = (e: ContainerSearchResult) => {
    return e.containerId === currentContainer?.containerId ? (
      <Select
        className="dropdown"
        expandToViewport
        data-test-id="subcategory-dropdown"
        selectedOption={editInput.subcategory}
        onChange={({ detail }) => {
          setEditInput({
            ...editInput,
            subcategory: detail.selectedOption?.value ? detail.selectedOption : null,
          });
        }}
        options={[
          {
            label: '-',
            value: '',
          },
          ...getSubcategoryOptions(categoriesMap, editInput.category),
        ]}
        placeholder={t('Choose subcategory')}
        empty={t('No options')}
        disabled={editInput.category === null || updateContainerLoading}
        invalid={subcategoryError.length > 0}
      />
    ) : (
      <div>
        {e.subcategory}
        <Button
          data-test-id="subcategory-edit-button"
          iconAlign="right"
          iconName="edit"
          variant="inline-icon"
          onClick={() => setCurrentContainer(e)}
          disabled={updateContainerLoading}
        />
      </div>
    );
  };

  const editIsCompliantSection = (e: ContainerSearchResult) => {
    return e.containerId === currentContainer?.containerId && !deleteContainerLoading ? (
      <RadioGroup
        data-test-id="is-label-printed-radio"
        value={editInput.isCompliant ? 'yes' : 'no'}
        items={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]}
        onChange={({ detail }) => {
          setEditInput({
            ...editInput,
            isCompliant: detail.value === 'yes',
          });
        }}
      />
    ) : (
      <div>
        {e.isCompliant ? 'Yes' : 'No'}
        <Button
          data-test-id="is-compliant-edit-button"
          iconAlign="right"
          iconName="edit"
          variant="inline-icon"
          onClick={() => setCurrentContainer(e)}
          disabled={updateContainerLoading || deleteContainerLoading}
        />
      </div>
    );
  };

  const editActionSection = (e: ContainerSearchResult) => {
    const rowHasValidationError =
      e.containerId === currentContainer?.containerId &&
      (sectionError.length > 0 ||
        aisleError.length > 0 ||
        levelError.length > 0 ||
        alphabetIdentifierError.length > 0 ||
        cellError.length > 0 ||
        categoryError.length > 0 ||
        subcategoryError.length > 0);
    const serverError = idToServerError.get(e.containerId);
    const rowHasServerError = !!(serverError && serverError !== SUCCESS);
    const rowHasSuccess = !!(serverError && serverError === SUCCESS);

    return (
      <SpaceBetween direction="vertical" size="xs">
        {e.containerId === currentContainer?.containerId &&
          !deleteContainerLoading &&
          !updateContainerLoading && (
            <SpaceBetween direction="horizontal" size="xs">
              <div className="flex row">
                <Link external href={`/assets-inventory/container/${e.containerId}`}>
                  View Container
                </Link>
              </div>
              <div>
                <Button
                  data-test-id="save-button"
                  disabled={
                    rowHasValidationError || rowHasSuccess || !changesWereMade(e, editInput)
                  }
                  onClick={() => {
                    handleUpdateContainer(e, editInput);
                  }}
                >
                  Save
                </Button>

                <Button
                  data-test-id="reset-button"
                  disabled={rowHasSuccess || !changesWereMade(e, editInput)}
                  onClick={() => {
                    setEditInput(getDefaultEditInput(e));
                    setIdToServerError(cloneDeep(idToServerError.set(e.containerId, '')));
                  }}
                >
                  Reset
                </Button>
              </div>
            </SpaceBetween>
          )}
        {e.containerId !== currentContainer?.containerId && isMovableTypeSelected && (
          <Button
            data-test-id="delete-button"
            disabled={rowHasSuccess || !e.isEmpty || deleteContainerLoading}
            onClick={() => {
              setCurrentContainer(e);
              handleDeleteContainer(e);
            }}
          >
            Delete
          </Button>
        )}
        {(rowHasServerError || rowHasValidationError) && (
          <Popover
            data-test-id="error-popover"
            position="top"
            size="large"
            content={
              <div>
                {sectionError.length > 0 && (
                  <StatusIndicator type="error">{`Section: ${sectionError}`}</StatusIndicator>
                )}
                {aisleError.length > 0 && (
                  <StatusIndicator type="error">{`Aisle: ${aisleError}`}</StatusIndicator>
                )}
                {levelError.length > 0 && (
                  <StatusIndicator type="error">{`Level: ${levelError}`}</StatusIndicator>
                )}
                {alphabetIdentifierError.length > 0 && (
                  <StatusIndicator type="error">{`Alphabet Identifier: ${alphabetIdentifierError}`}</StatusIndicator>
                )}
                {cellError.length > 0 && (
                  <StatusIndicator type="error">{`Cell: ${cellError}`}</StatusIndicator>
                )}
                {categoryError.length > 0 && (
                  <StatusIndicator type="error">{categoryError}</StatusIndicator>
                )}
                {subcategoryError.length > 0 && (
                  <StatusIndicator type="error">{subcategoryError}</StatusIndicator>
                )}
                {rowHasServerError && (
                  <StatusIndicator data-test-id="server-error-status" type="error">
                    {idToServerError.get(e.containerId)}
                  </StatusIndicator>
                )}
              </div>
            }
          >
            <StatusIndicator type="error">Error</StatusIndicator>
          </Popover>
        )}

        {e.containerId === currentContainer?.containerId && updateContainerLoading && (
          <StatusIndicator type="in-progress">Updating</StatusIndicator>
        )}
        {e.containerId === currentContainer?.containerId && deleteContainerLoading && (
          <StatusIndicator type="in-progress">Deleting</StatusIndicator>
        )}
        {rowHasSuccess && <StatusIndicator type="success">Saved</StatusIndicator>}
      </SpaceBetween>
    );
  };

  useEffect(() => {
    if (currentContainer) {
      // update container type so we get the correct container definitions
      handleTypeChange(currentContainer?.containerType);

      setEditInput(getDefaultEditInput(currentContainer));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContainer]);

  useEffect(() => {
    if (containerDefinitionMap) {
      setEditInput(getDefaultEditInput(currentContainer));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerDefinitionMap]);

  useEffect(() => {
    if (containerDefinition && currentContainer && !isMovableTypeSelected) {
      const newEditInput: EditInput = {
        section: currentContainer.section?.toUpperCase() ?? '',
        aisle: currentContainer.aisle?.toUpperCase() ?? '',
        level: currentContainer.level?.toUpperCase() ?? '',
        alphabetIdentifier: currentContainer.alphabetIdentifier?.toUpperCase() ?? '',
        cell: currentContainer.cell?.toUpperCase() ?? '',
        category: editInput.category,
        subcategory: editInput.subcategory,
      };

      const currentContainerDefinition = currentContainer.containerDefinitionId
        ? containerDefinitionMap?.get(currentContainer.containerDefinitionId) ?? null
        : null;

      if (
        currentContainerDefinition?.category === 'Default' &&
        currentContainerDefinition.subcategory === 'Default' &&
        isObjectDefined(containerDefinition.metadataConfiguration?.level?.default) &&
        currentContainer.cell.length > 6
      ) {
        newEditInput.alphabetIdentifier = currentContainer.cell.substring(0, 1)?.toUpperCase();
        newEditInput.section = currentContainer.cell.substring(1, 2)?.toUpperCase();
        newEditInput.aisle = currentContainer.cell.substring(2, 4)?.toUpperCase();
        newEditInput.level = currentContainer.cell.substring(4, 6)?.toUpperCase();
        newEditInput.cell = currentContainer.cell
          .substring(6, currentContainer.cell.length)
          .toUpperCase();
      } else {
        // if there is no section, aisle, level, etc. for this definition, move it from there to cell
        if (!isObjectDefined(containerDefinition.metadataConfiguration?.level?.default)) {
          newEditInput.cell = newEditInput.level + newEditInput.cell;
          newEditInput.level = '';
        }
        if (!isObjectDefined(containerDefinition.metadataConfiguration?.aisle?.default)) {
          newEditInput.cell = newEditInput.aisle + newEditInput.cell;
          newEditInput.aisle = '';
        }
        if (!isObjectDefined(containerDefinition.metadataConfiguration?.section?.default)) {
          newEditInput.cell = newEditInput.section + newEditInput.cell;
          newEditInput.section = '';
        }
        if (
          !isObjectDefined(containerDefinition.metadataConfiguration?.alphabetIdentifier?.default)
        ) {
          newEditInput.cell = newEditInput.alphabetIdentifier + newEditInput.cell;
          newEditInput.alphabetIdentifier = '';
        }
      }

      setEditInput(newEditInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerDefinition]);

  useEffect(() => {
    if (editInput && currentContainer && !isMovableTypeSelected) {
      // set container definition if subcategory is chosen, otherwise use current container's
      let containerDefinitionId = editInput.subcategory?.value;
      if (!containerDefinitionId) {
        if (currentContainer) {
          containerDefinitionId = currentContainer.containerDefinitionId;
        }
      }
      const newContainerDefinition = containerDefinitionId
        ? containerDefinitionMap?.get(containerDefinitionId) ?? null
        : null;
      setContainerDefinition(newContainerDefinition);

      // clear serverError if there was a success and user made an edit
      const serverError = idToServerError.get(currentContainer.containerId);
      if (serverError && serverError === SUCCESS) {
        setIdToServerError(idToServerError.set(currentContainer.containerId, ''));
      }

      // do validations
      setCategoryError(editInput.category === null ? 'Category cannot be empty' : '');
      setSubcategoryError(
        editInput.category !== null && editInput.subcategory === null
          ? 'Subcategory cannot be empty'
          : ''
      );
      setSectionError(validateInput(editInput.section, false, true));
      setAisleError(validateInput(editInput.aisle, false, true));
      setLevelError(validateInput(editInput.level, false, true));
      setAlphaBetIdentifierError(validateInput(editInput.alphabetIdentifier, false, true));

      let newCellError = validateInput(editInput.cell, true, true);
      const containerNamePrefix =
        newContainerDefinition?.metadataConfiguration.containerNamePrefix?.default;
      if (
        !newCellError &&
        !!containerNamePrefix &&
        !editInput.cell.startsWith(containerNamePrefix)
      ) {
        newCellError = `Container name must begin with prefix ${containerNamePrefix}`;
      }
      setCellError(newCellError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editInput]);

  useEffect(() => {
    setEditedSearchResults(cloneDeep(searchResults));
    setCurrentContainer(null);
    setIdToServerError(new Map());
  }, [searchResults]);

  useEffect(() => {
    setSearchOptions({
      pageSize: preferences.pageSize ?? 10,
      sortingColumn: serverSortingColumn,
      sortingDescending,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, preferences.pageSize, serverSortingColumn, sortingDescending]);

  return (
    <Table
      {...collectionProps}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      selectedItems={selectedItems}
      onSortingChange={({ detail }) => {
        setSortingColumn(detail.sortingColumn);
        const serverSortingColumnValue = SORTING_FIELD_TO_SERVER_SORTING_COLUMN.get(
          detail.sortingColumn?.sortingField ?? ''
        );
        if (serverSortingColumnValue) {
          setServerSortingColumn(serverSortingColumnValue);
          setSortingDescending(detail.isDescending ?? false);
        }
      }}
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      ariaLabels={{
        selectionGroupLabel: t('Items selection'),
        // eslint-disable-next-line no-shadow
        allItemsSelectionLabel: ({ selectedItems }) =>
          t(`${selectedItems.length} item(s) selected`),
        // eslint-disable-next-line no-shadow
        itemSelectionLabel: ({ selectedItems }, item) => {
          const isItemSelected = selectedItems.some((i) => i.name === item.name);
          return t(`${item.name} is${isItemSelected ? ' ' : ' not '}selected`);
        },
      }}
      columnDefinitions={columnDefinitions}
      items={items}
      loadingText={t('Loading resources')}
      selectionType={showMigrationFeature && editMode ? undefined : 'multi'}
      trackBy="containerId"
      visibleColumns={getVisibleColumns()}
      empty={
        <Box textAlign="center" color="inherit">
          <b>{t('No containers')}</b>
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            {t('No containers to display.')}
          </Box>
        </Box>
      }
      header={
        <Header
          counter={
            selectedItems.length ? `(${selectedItems.length}/${totalResults})` : `(${totalResults})`
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {managePermissions && showMigrationFeature && (
                <Toggle
                  className="toggle"
                  disabled={!searchVariables?.type}
                  onChange={({ detail }): void => setEditMode(detail.checked)}
                  checked={editMode}
                >
                  Edit mode
                </Toggle>
              )}
              {selectedItems.length === 0 ? (
                <Button onClick={downloadAllContainers} disabled={totalResults === 0}>
                  {t(`Download All Results (${totalResults})`)}
                </Button>
              ) : (
                <Button onClick={() => downloadSelectedContainers(selectedItems)}>
                  {t(`Download Selected Results (${selectedItems.length})`)}
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {t('Containers')}
        </Header>
      }
      pagination={
        <Pagination
          {...paginationProps}
          currentPageIndex={pageIndex}
          pagesCount={ceil(totalResults / (preferences.pageSize ?? 10))}
          ariaLabels={{
            nextPageLabel: t('Next page'),
            previousPageLabel: t('Previous page'),
            pageLabel: (pageNum) => t(`Page ${pageNum} of all pages`),
          }}
          onChange={({ detail }) => {
            setPageIndex(detail.currentPageIndex);
          }}
        />
      }
      preferences={
        <CollectionPreferences
          title={t('Preferences')}
          onConfirm={({ detail }) => setPreferences(detail)}
          confirmLabel={t('Confirm')}
          cancelLabel={t('Cancel')}
          preferences={preferences}
          pageSizePreference={{
            title: t('Select page size'),
            options: [
              { value: 10, label: t('10 items') },
              { value: 20, label: t('20 items') },
              { value: 50, label: t('50 items') },
              { value: 100, label: t('100 items') },
              { value: 500, label: t('500 items') },
            ],
          }}
          visibleContentPreference={
            editMode
              ? undefined
              : {
                  title: t('Select visible content'),
                  options: [
                    {
                      label: t('Main distribution properties'),
                      options: [
                        {
                          id: 'name',
                          label: t('Name'),
                          editable: false,
                        },
                        { id: 'containerType', label: t('Type') },
                        { id: 'category', label: t('Category') },
                        { id: 'subcategory', label: t('Subcategory') },
                        { id: 'location', label: t('Location') },
                        { id: 'rules', label: t('Rules') },
                        { id: 'status', label: t('State') },
                        { id: 'seal_ids', label: t('Seal IDs') },
                        { id: 'ticket_ids', label: t('Ticket IDs') },
                        { id: 'cctvRequired', label: t('CCTV Coverage') },
                        { id: 'createdBy', label: t('Created By') },
                        { id: 'lastUpdatedUser', label: t('Last Updated By') },
                        { id: 'creationTime', label: t('Creation Time') },
                        { id: 'lastUpdatedTime', label: t('Last Update Time') },
                        { id: 'hasOverriddenRules', label: t('Overridden Rules') },
                        { id: 'isCompliant', label: t('Compliant') },
                        { id: 'parentContainerName', label: t('Parent Container Name') },
                        { id: 'parentContainerType', label: t('Parent Container Type') },
                        { id: 'userAlias', label: t('User with Custody') },
                        ...(zoneFeatureLaunched ? [{ id: 'zone', label: t('Zone') }] : []),
                        ...(containerCapacityFeature
                          ? [{ id: 'isEmpty', label: t('Container capacity') }]
                          : []),
                      ],
                    },
                  ],
                }
          }
        />
      }
    />
  );
}
